// ContractRollupStrip — the contract-scoped PO rollup strip. Unlike
// FinancePositionBanner (which is scoped to the selected purchase order),
// this strip sums every PO on the contract (Task 1.7's computeContractRollup)
// against the contract's headline value, so it renders once above
// PoContextBar regardless of which PO is selected. Committed, contract
// value, and remaining are all ex-GST (PO totals carry no GST split at the
// header level — GST is decided per PoLine), so a single ex-GST meta note
// covers all three figures rather than repeating it per figure. When the
// contract's purchase orders collectively exceed its value, an over-commit
// badge names the overage amount. Pure/presentational — the derived rollup
// is passed in by FinanceTab, sourced from the finance summary response.

import { formatAud } from "../../../utils/financeCalc";
import { gstSuffix } from "../../../utils/gstSuffix";
import type { ContractRollup } from "../../../types/finance";

interface ContractRollupStripProps {
  rollup: ContractRollup;
}

interface Figure {
  key: string;
  label: string;
  value: number;
}

export default function ContractRollupStrip({ rollup }: ContractRollupStripProps) {
  const { committed, contractValue, remaining, overCommitted } = rollup;
  const overageAmount = -remaining;

  const figures: Figure[] = [
    { key: "committed", label: "Committed", value: committed },
    { key: "contract-value", label: "Contract value", value: contractValue },
    { key: "remaining", label: "Remaining", value: remaining },
  ];

  return (
    <section
      aria-label="Contract purchase order rollup"
      className="rounded-lg border border-slate-200/60 bg-white p-4 shadow-soft-sm dark:border-slate-700 dark:bg-slate-800 dark:shadow-soft-sm-dark"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="grid grid-cols-3 gap-4">
          {figures.map((figure) => (
            <div key={figure.key}>
              <div className="text-overline uppercase text-slate-500 dark:text-slate-400">
                {figure.label}
              </div>
              <div className="mt-1 text-h4 tabular-nums text-slate-900 dark:text-slate-100">
                {formatAud(figure.value)}
              </div>
            </div>
          ))}
        </div>

        {overCommitted && (
          <span className="badge-filledRed shrink-0">
            Over-committed by {formatAud(overageAmount)}
          </span>
        )}
      </div>

      <div className="mt-2 text-caption text-slate-500 dark:text-slate-400">
        {gstSuffix(true)}
      </div>
    </section>
  );
}
